import React from "react";
import {
    Box, Heading, Text, VStack, HStack, SimpleGrid, Badge, Icon, Card, Flex, Button, Divider, Link, IconButton, useToast
} from "@chakra-ui/react";
import { FiClock, FiUsers, FiTarget, FiCalendar, FiMapPin, FiAward, FiVideo, FiCopy } from "react-icons/fi";
import { FaCalendarPlus } from "react-icons/fa";
import moment from "moment";
import { formatTime12h, getGoogleMapsSearchUrl, getOrdinalSuffix } from "../utils/formatHelpers";
import { getJoinButtonState, openMeetingLink, copyMeetingLink } from "../utils/meetingHelpers";

export const CourseOverviewTab = ({ courseMaterials, handleDownloadSessionCalendar, handleDownloadAllSessionsCalendar }) => {
    const toast = useToast();

    const handleCopyMeetingLink = async (url) => {
        const ok = await copyMeetingLink(url);
        toast({
            title: ok ? "Link copied" : "Could not copy link",
            description: ok ? "Meeting link copied to clipboard." : "Please copy the link manually.",
            status: ok ? "success" : "error",
            duration: 2500,
            isClosable: true,
        });
    };

    return (
        <VStack align="stretch" spacing={4}>
            {/* Overview Stats */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 3, md: 4 }}>
                {courseMaterials?.course_type?.course_duration && (
                    <HStack>
                        <Icon as={FiClock} color="blue.500" boxSize={{ base: 4, md: 5 }} />
                        <Box>
                            <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">Duration</Text>
                            <Text fontWeight="600" fontSize={{ base: "sm", md: "md" }}>{courseMaterials.course_type.course_duration} hours</Text>
                        </Box>
                    </HStack>
                )}
                {courseMaterials?.max_capacity && (
                    <HStack>
                        <Icon as={FiUsers} color="purple.500" boxSize={{ base: 4, md: 5 }} />
                        <Box>
                            <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">Capacity</Text>
                            <Text fontWeight="600" fontSize={{ base: "sm", md: "md" }}>{courseMaterials.max_capacity} students</Text>
                        </Box>
                    </HStack>
                )}
                {courseMaterials?.attendance_req && (
                    <HStack>
                        <Icon as={FiTarget} color="red.500" boxSize={{ base: 4, md: 5 }} />
                        <Box>
                            <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500">Attendance Required</Text>
                            <Text fontWeight="600" fontSize={{ base: "sm", md: "md" }}>{courseMaterials.attendance_req}%</Text>
                        </Box>
                    </HStack>
                )}
            </SimpleGrid>

            <Divider />

            {/* Schedule Information */}
            <VStack align="stretch" spacing={3}>
                <HStack spacing={2} color="gray.600" flexWrap="nowrap">
                    <Icon as={FiCalendar} flexShrink={0} />
                    <Text fontWeight="600" fontSize={{ base: "xs", md: "sm" }} flexShrink={0}>Date Range</Text>
                    <Text fontSize={{ base: "xs", md: "sm" }} whiteSpace="nowrap">
                        {moment(courseMaterials?.start_date).format("DD MMM YYYY")} - {moment(courseMaterials?.end_date).format("DD MMM YYYY")}
                    </Text>
                </HStack>

                {courseMaterials?.course_dates && Array.isArray(courseMaterials.course_dates) && courseMaterials.course_dates.length > 0 && (
                    <Box>
                        <Flex justify="space-between" align="center" mb={3}>
                            <Text fontWeight="600" fontSize={{ base: "xs", md: "sm" }} color="purple.600">
                                <Icon as={FiCalendar} mr={2} />Class Schedule
                            </Text>
                            <Button
                                size="xs" colorScheme="purple" variant="outline"
                                leftIcon={<Icon as={FaCalendarPlus} boxSize={{ base: 3, md: 4 }} />}
                                onClick={handleDownloadAllSessionsCalendar}
                                fontSize={{ base: "2xs", md: "xs" }}
                            >
                                Add All to Calendar
                            </Button>
                        </Flex>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                            {courseMaterials.course_dates.map((schedule, idx) => {
                                const scheduleDate = moment(schedule.date);
                                const today = moment().startOf('day');
                                const daysDifference = scheduleDate.diff(today, 'days');
                                const isPast = daysDifference < 0;
                                const isToday = daysDifference === 0;
                                const joinState = getJoinButtonState(schedule);
                                const isOnline = !!schedule.is_online || !!joinState.meetingUrl;

                                let badgeColor = 'gray';
                                let badgeText = '';
                                if (joinState.status === "ended" || isPast) { badgeColor = 'gray'; badgeText = 'Completed'; }
                                else if (isToday) { badgeColor = 'green'; badgeText = 'Ongoing'; }
                                else { badgeColor = 'blue'; badgeText = `In ${daysDifference} ${daysDifference === 1 ? 'day' : 'days'}`; }

                                return (
                                    <Card key={idx} p={{ base: 2, md: 3 }} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                                        <VStack align="start" spacing={1.5} w="100%">
                                            <HStack justify="space-between" w="100%">
                                                <HStack spacing={1.5} flex="1" minW={0}>
                                                    <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="600" whiteSpace="nowrap">
                                                        {moment(schedule.date).format("ddd, DD MMM YYYY")}
                                                    </Text>
                                                    <Badge colorScheme={badgeColor} fontSize="2xs" flexShrink={0}>{badgeText}</Badge>
                                                </HStack>
                                                <IconButton
                                                    icon={<Icon as={FaCalendarPlus} />}
                                                    size="xs" colorScheme="purple" variant="ghost"
                                                    aria-label="Add to Calendar" title="Add to Calendar"
                                                    flexShrink={0}
                                                    onClick={() => handleDownloadSessionCalendar(
                                                        schedule,
                                                        courseMaterials?.course_type?.name || "Module",
                                                        courseMaterials.course_dates
                                                    )}
                                                />
                                            </HStack>
                                            {schedule.start_time && schedule.end_time && (
                                                <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.600">
                                                    {formatTime12h(schedule.start_time)} – {formatTime12h(schedule.end_time)}
                                                </Text>
                                            )}
                                            {schedule.room && (
                                                <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.600">
                                                    {isOnline ? "Meeting" : "Room"}: {schedule.room}
                                                </Text>
                                            )}
                                            {joinState.meetingUrl && (
                                                <HStack spacing={2} pt={1} flexWrap="wrap">
                                                    {joinState.status === "join" ? (
                                                        <Button
                                                            size="xs"
                                                            colorScheme="green"
                                                            variant="solid"
                                                            leftIcon={<Icon as={FiVideo} />}
                                                            onClick={() => openMeetingLink(joinState.meetingUrl)}
                                                        >
                                                            Join Now
                                                        </Button>
                                                    ) : joinState.status === "countdown" ? (
                                                        <Button
                                                            size="xs"
                                                            colorScheme="blue"
                                                            variant="solid"
                                                            onClick={() => openMeetingLink(joinState.meetingUrl)}
                                                        >
                                                            {joinState.label}
                                                        </Button>
                                                    ) : null}
                                                    <Button
                                                        size="xs"
                                                        variant="outline"
                                                        leftIcon={<Icon as={FiCopy} />}
                                                        onClick={() => handleCopyMeetingLink(joinState.meetingUrl)}
                                                    >
                                                        Copy link
                                                    </Button>
                                                </HStack>
                                            )}
                                        </VStack>
                                    </Card>
                                );
                            })}
                        </SimpleGrid>
                        {(courseMaterials.course_dates || []).some((d) => d.is_online || d.meeting_url || d.online_meeting_details?.join_url) && (
                            <Text fontSize="xs" color="gray.500" mt={2}>
                                Some sessions are online — use Join Now / Copy link in the schedule above.
                            </Text>
                        )}
                    </Box>
                )}
            </VStack>

            <Divider />

            {/* Venue and Trainer Information */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {(courseMaterials?.venue || courseMaterials?.venues) && (
                    <Box>
                        <HStack mb={2}>
                            <Icon as={FiMapPin} color="green.500" boxSize={{ base: 4, md: 5 }} />
                            <Text fontWeight="600" fontSize={{ base: "xs", md: "sm" }}>Venue Details</Text>
                        </HStack>
                        <VStack align="start" spacing={1} pl={7}>
                            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="600">
                                {courseMaterials?.venues?.name || courseMaterials?.venue}
                            </Text>
                            {(courseMaterials?.venues?.address || courseMaterials?.venues?.postal_code) && (() => {
                                const mapsUrl = getGoogleMapsSearchUrl(courseMaterials.venues?.address, courseMaterials.venues?.postal_code);
                                const addressLine = courseMaterials.venues?.address || "";
                                const postalLine = courseMaterials.venues?.postal_code ? `Singapore ${courseMaterials.venues.postal_code}` : "";
                                const fullDisplay = [addressLine, postalLine].filter(Boolean).join(", ");
                                if (!fullDisplay) return null;
                                return mapsUrl ? (
                                    <Link href={mapsUrl} isExternal fontSize="xs" color="blue.600" fontWeight="500" _hover={{ textDecoration: "underline" }} display="inline-flex" alignItems="flex-start" gap={1} flexDirection="column">
                                        <span>{addressLine}</span>
                                        {postalLine && <span>{postalLine}</span>}
                                    </Link>
                                ) : (
                                    <Text fontSize="xs" color="gray.600">{fullDisplay}</Text>
                                );
                            })()}
                            {courseMaterials?.rooms && (() => {
                                const formatFloorUnit = () => {
                                    if (!courseMaterials.rooms.floor && !courseMaterials.rooms.unit) return "";
                                    const parts = [];
                                    if (courseMaterials.rooms.floor) {
                                        const floor = courseMaterials.rooms.floor;
                                        const floorText = isNaN(floor) ? floor : `${floor}${getOrdinalSuffix(Number(floor))} Floor`;
                                        parts.push(floorText);
                                    }
                                    if (courseMaterials.rooms.unit) parts.push(`Unit ${courseMaterials.rooms.unit}`);
                                    return parts.length > 0 ? ` (${parts.join(", ")})` : "";
                                };
                                return (
                                    <Text fontSize="xs" color="gray.600" mt={1}>
                                        🏫 {courseMaterials.rooms.name}{formatFloorUnit()}
                                        {courseMaterials.rooms.capacity && ` - Capacity: ${courseMaterials.rooms.capacity}`}
                                    </Text>
                                );
                            })()}
                        </VStack>
                    </Box>
                )}

                {courseMaterials?.trainers && (
                    <Box>
                        <HStack mb={2}>
                            <Icon as={FiUsers} color="blue.500" boxSize={{ base: 4, md: 5 }} />
                            <Text fontWeight="600" fontSize={{ base: "xs", md: "sm" }}>Trainer</Text>
                        </HStack>
                        <VStack align="start" spacing={1} pl={7}>
                            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="600">{courseMaterials.trainers.name}</Text>
                        </VStack>
                    </Box>
                )}
            </SimpleGrid>

            {/* Course Details */}
            {(courseMaterials?.course_type?.course_objectives || courseMaterials?.course_type?.prerequisites || courseMaterials?.course_type?.minimum_entry_requirements) && (
                <>
                    <Divider />
                    <Box>
                        <Heading fontSize={{ base: "sm", md: "md" }} mb={4} color="brand.700">
                            <Icon as={FiTarget} mr={2} />Course Details
                        </Heading>
                        {courseMaterials?.course_type?.course_objectives && (
                            <Box mb={4}>
                                <Text fontWeight="600" mb={2} fontSize={{ base: "xs", md: "sm" }} color="gray.700">Course Objectives</Text>
                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" pl={4} borderLeft="2px solid" borderColor="blue.200">
                                    {courseMaterials.course_type.course_objectives}
                                </Text>
                            </Box>
                        )}
                        {courseMaterials?.course_type?.prerequisites && (
                            <Box mb={4}>
                                <Text fontWeight="600" mb={2} fontSize={{ base: "xs", md: "sm" }} color="gray.700">Prerequisites</Text>
                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" pl={4} borderLeft="2px solid" borderColor="orange.200">
                                    {courseMaterials.course_type.prerequisites}
                                </Text>
                            </Box>
                        )}
                        {courseMaterials?.course_type?.minimum_entry_requirements && (
                            <Box mb={4}>
                                <Text fontWeight="600" mb={2} fontSize={{ base: "xs", md: "sm" }} color="gray.700">Entry Requirements</Text>
                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" pl={4} borderLeft="2px solid" borderColor="green.200">
                                    {courseMaterials.course_type.minimum_entry_requirements}
                                </Text>
                            </Box>
                        )}
                    </Box>
                </>
            )}

            {/* Certification Info */}
            {courseMaterials?.course_type?.certification && (
                <>
                    <Divider />
                    <Box>
                        <HStack mb={2}>
                            <Icon as={FiAward} color="yellow.500" boxSize={{ base: 4, md: 5 }} />
                            <Text fontWeight="600" fontSize={{ base: "xs", md: "sm" }}>Certification</Text>
                        </HStack>
                        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" pl={7}>
                            {courseMaterials.course_type.certification}
                        </Text>
                    </Box>
                </>
            )}
        </VStack>
    );
};
